import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function CAPMCalculator() {
  const [riskFreeRate, setRiskFreeRate] = useState("");
  const [beta, setBeta] = useState("");
  const [marketReturn, setMarketReturn] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const rf = parseFloat(riskFreeRate) / 100;
    const b = parseFloat(beta);
    const rm = parseFloat(marketReturn) / 100;

    if (isNaN(rf) || isNaN(b) || isNaN(rm)) {
      toast.error("Please enter valid values");
      return;
    }

    // CAPM: Expected Return = Rf + β(Rm - Rf)
    const expectedReturn = (rf + b * (rm - rf)) * 100;

    setResult(expectedReturn);
    toast.success("Expected return calculated!");
  };

  const reset = () => {
    setRiskFreeRate("");
    setBeta("");
    setMarketReturn("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>CAPM Calculator</CardTitle>
          <CardDescription>
            Calculate expected return using Capital Asset Pricing Model
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="riskFree">Risk-Free Rate (%)</Label>
            <Input
              id="riskFree"
              type="number"
              placeholder="3"
              value={riskFreeRate}
              onChange={(e) => setRiskFreeRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="beta">Beta (β)</Label>
            <Input
              id="beta"
              type="number"
              placeholder="1.2"
              value={beta}
              onChange={(e) => setBeta(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="market">Expected Market Return (%)</Label>
            <Input
              id="market"
              type="number"
              placeholder="10"
              value={marketReturn}
              onChange={(e) => setMarketReturn(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>Expected Return:</strong> {result.toFixed(2)}%
                </p>
                <CopyResultButton value={result.toFixed(2)} />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Formula: E(R) = Rf + β(Rm - Rf)
              </p>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["capm"]} trigger={result} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
